import React, { useEffect, useCallback, useState } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import Placeholder from "@tiptap/extension-placeholder";
import Youtube from "@tiptap/extension-youtube";
import {
  Bold, Italic, List, ListOrdered, Quote, Undo, Redo, Link2, Image as ImageIcon,
  Images, Youtube as YoutubeIcon, Minus, Heading1, Heading2, Heading3,
} from "lucide-react";
import { ATID } from "@/lib/adminTestIds";
import MediaPicker from "@/components/admin/MediaPicker";

function ToolbarBtn({ onClick, active, disabled, title, children, touch }) {
  return (
    <button
      type="button"
      title={title}
      onClick={onClick}
      disabled={disabled}
      aria-label={title}
      className={`inline-flex items-center justify-center ${touch ? "h-10 w-10" : "h-8 w-8"} border border-softblue text-nightblue transition-colors ${
        active ? "bg-nightblue text-white border-nightblue" : "bg-white hover:bg-lightgray"
      } disabled:opacity-40 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

export default function RichTextEditor({
  value, onChange, placeholder = "Escribí el contenido de la noticia…",
  testId = ATID.editorContent, mediaLibrary = true, touch = false,
}) {
  const [pickerOpen, setPickerOpen] = useState(false);
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
      Link.configure({ openOnClick: false, autolink: true, HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" } }),
      Image.configure({ HTMLAttributes: { class: "max-w-full h-auto" } }),
      Youtube.configure({ controls: true, HTMLAttributes: { class: "w-full aspect-video" } }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || "",
    onUpdate({ editor }) {
      onChange?.(editor.getHTML());
    },
    editorProps: {
      attributes: {
        class: `article-body ${touch ? "min-h-[260px] text-[17px]" : "min-h-[320px]"} p-4 focus:outline-none`,
        "data-testid": testId,
      },
    },
  });

  // Sync incoming value updates only when the editor is created and value diverges (e.g. loading edit form).
  useEffect(() => {
    if (!editor) return;
    const current = editor.getHTML();
    if ((value || "") !== current) {
      editor.commands.setContent(value || "", false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  const addLink = useCallback(() => {
    if (!editor) return;
    const prev = editor.getAttributes("link").href || "";
    const url = window.prompt("URL del enlace:", prev);
    if (url === null) return;
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor.chain().focus().extendMarkRange("link").setLink({ href: url }).run();
  }, [editor]);

  const addImage = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("URL de la imagen:");
    if (!url) return;
    editor.chain().focus().setImage({ src: url }).run();
  }, [editor]);

  const addImageFromLibrary = useCallback((media) => {
    if (!editor || !media) return;
    const src = media.variants?.inline?.url || media.url;
    editor.chain().focus().setImage({ src, alt: media.alt || media.original_name }).run();
  }, [editor]);

  const addYoutube = useCallback(() => {
    if (!editor) return;
    const url = window.prompt("URL del video de YouTube:");
    if (!url) return;
    editor.commands.setYoutubeVideo({ src: url, width: 640, height: 360 });
  }, [editor]);

  if (!editor) {
    return <div className="border border-softblue bg-white min-h-[320px] animate-pulse" />;
  }

  return (
    <div className="border border-softblue bg-white">
      <div className="flex flex-wrap items-center gap-1 p-2 border-b border-softblue bg-lightgray">
        <ToolbarBtn touch={touch} title="Deshacer" onClick={() => editor.chain().focus().undo().run()} disabled={!editor.can().undo()}>
          <Undo className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn touch={touch} title="Rehacer" onClick={() => editor.chain().focus().redo().run()} disabled={!editor.can().redo()}>
          <Redo className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-6 bg-softblue mx-1" />
        <ToolbarBtn touch={touch} title="Título 1" onClick={() => editor.chain().focus().toggleHeading({ level: 1 }).run()} active={editor.isActive("heading", { level: 1 })}>
          <Heading1 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn touch={touch} title="Título 2" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()} active={editor.isActive("heading", { level: 2 })}>
          <Heading2 className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn touch={touch} title="Título 3" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()} active={editor.isActive("heading", { level: 3 })}>
          <Heading3 className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-6 bg-softblue mx-1" />
        <ToolbarBtn touch={touch} title="Negrita" onClick={() => editor.chain().focus().toggleBold().run()} active={editor.isActive("bold")}>
          <Bold className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn touch={touch} title="Cursiva" onClick={() => editor.chain().focus().toggleItalic().run()} active={editor.isActive("italic")}>
          <Italic className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn touch={touch} title="Cita" onClick={() => editor.chain().focus().toggleBlockquote().run()} active={editor.isActive("blockquote")}>
          <Quote className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-6 bg-softblue mx-1" />
        <ToolbarBtn touch={touch} title="Lista" onClick={() => editor.chain().focus().toggleBulletList().run()} active={editor.isActive("bulletList")}>
          <List className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn touch={touch} title="Lista numerada" onClick={() => editor.chain().focus().toggleOrderedList().run()} active={editor.isActive("orderedList")}>
          <ListOrdered className="w-4 h-4" />
        </ToolbarBtn>
        <div className="w-px h-6 bg-softblue mx-1" />
        <ToolbarBtn touch={touch} title="Enlace" onClick={addLink} active={editor.isActive("link")}>
          <Link2 className="w-4 h-4" />
        </ToolbarBtn>
        {mediaLibrary && (
          <ToolbarBtn touch={touch} title="Insertar imagen (biblioteca)" onClick={() => setPickerOpen(true)}>
            <Images className="w-4 h-4" />
          </ToolbarBtn>
        )}
        <ToolbarBtn touch={touch} title="Insertar imagen por URL" onClick={addImage}>
          <ImageIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn touch={touch} title="Video de YouTube" onClick={addYoutube}>
          <YoutubeIcon className="w-4 h-4" />
        </ToolbarBtn>
        <ToolbarBtn touch={touch} title="Separador" onClick={() => editor.chain().focus().setHorizontalRule().run()}>
          <Minus className="w-4 h-4" />
        </ToolbarBtn>
      </div>
      <EditorContent editor={editor} />
      {mediaLibrary && (
        <MediaPicker
          open={pickerOpen}
          onOpenChange={setPickerOpen}
          onSelect={addImageFromLibrary}
          title="Insertar imagen desde la biblioteca"
          accept="image"
        />
      )}
    </div>
  );
}
